import React, { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import CustomerTabsJobFile from "../../components/CustomerTabsJobFile";
import JobFileInvoiceTable from "../../components/Table/JobFileInvoiceTable";
import InvoiceEditModal from "../../components/Modal/InvoiceEditModal";
import AddInvoiceModal from "../../components/Modal/AddInvoiceModal";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import { Icon } from "@iconify/react";
import { openReceiptReportByPayRow } from "../../../utils/ReportUtils";

export default function JobFileInvoicePage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false);
  const [invoiceDataForAdd, setInvoiceDataForAdd] = useState(null);

  const job = location.state?.job || null;

  useEffect(() => {
    fetchInvoiceData();
  }, [id]);

  const fetchInvoiceData = async () => {
    try {
      setLoading(true);
      
      // ดึงข้อมูลสัญญา
      const contractRes = await api.get(`/get-contract/${id}`);
      if (contractRes.data.status !== "OK") {
        throw new Error(contractRes.data.message || "ไม่สามารถดึงข้อมูลสัญญาได้");
      }

      const header = contractRes.data.header || {};
      setData({
        Contract_No: header.Doc_No || id,
        CustomerName: header.DisplayName || `${header.AR_Name || ""} ${header.AR_LastName || ""}`.trim() || "-",
      });

      // ดึงข้อมูล invoice จาก CT_Pay
      const invoiceRes = await api.get(`/get-contract-invoices/${id}`);
      if (invoiceRes.data.status === "OK") {
        const invoiceList = (invoiceRes.data.result || []).map((inv) => {
          // กำหนดสถานะ
          let status = "pending";
          if (inv.Invoice_Status === 1) {
            status = "paid";
          } else if (inv.Invoice_Status === 2) {
            status = "overdue";
          } else {
            // ตรวจสอบว่าเกินกำหนดหรือไม่ (ถ้า CT_Due_Date ผ่านมาแล้วและยังไม่ชำระ)
            if (inv.CT_Due_Date) {
              const payDate = new Date(inv.CT_Due_Date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              if (payDate < today && inv.Invoice_Status === 0) {
                status = "overdue";
              }
            }
          }

          return {
            CT_Pay_Id: inv.CT_Pay_Id,
            Doc_Id: inv.Doc_Id, // เพิ่ม Doc_Id
            Invoice_No: inv.Invoice_No || "-",
            Line: inv.Line,
            Date: inv.CT_Due_Date 
              ? new Date(inv.CT_Due_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            CT_Due_Date: inv.CT_Due_Date, // เพิ่ม CT_Due_Date ต้นฉบับ
            Due_Date: inv.Due_Date || null, // เพิ่ม Due_Date
            Total: inv.CT_Due_Amount || 0,
            ToPay: inv.CT_Due_Amount || 0,
            Paid: inv.Paid_Amount || 0,
            PayDate: inv.Paid_Date
              ? new Date(inv.Paid_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            Remark: inv.Remark || "-",
            Ref1: inv.Ref1 || "-",
            Ref2: inv.Ref2 || "-",
            Payment_Method:
              inv.Payment_Method != null && inv.Payment_Method !== ""
                ? String(inv.Payment_Method)
                : "1",
            Withholding_Tax:
              inv.Withholding_Tax === 1 || inv.Withholding_Tax === true,
            Bank:
              inv.Bank && String(inv.Bank).trim() !== "" ? inv.Bank : "-",
            Check_No:
              inv.Check_No && inv.Check_No !== "" ? inv.Check_No : "-",
            Check_Date: inv.Check_Date
              ? new Date(inv.Check_Date).toLocaleDateString("th-TH", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
              : "-",
            Transfer_Bank:
              inv.Transfer_Bank && String(inv.Transfer_Bank).trim() !== ""
                ? inv.Transfer_Bank
                : "-",
            Transfer_Account:
              inv.Transfer_Account && inv.Transfer_Account !== ""
                ? inv.Transfer_Account
                : "-",
            Card_Bank:
              inv.Card_Bank && String(inv.Card_Bank).trim() !== ""
                ? inv.Card_Bank
                : "-",
            Card_No: inv.Card_No && inv.Card_No !== "" ? inv.Card_No : "-",
            Status: status,
            Invoice_Status: inv.Invoice_Status || 0,
            Is_VAT: inv.Is_VAT === 1 || inv.Is_VAT === true,
          };
        });

        setInvoices(invoiceList);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: error.message || "ไม่สามารถดึงข้อมูลใบแจ้งหนี้ได้",
      });
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleModalClose = () => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
  };

  const handleModalSuccess = () => {
    fetchInvoiceData(); // รีเฟรชข้อมูลหลังจากบันทึกสำเร็จ
  };

  const handlePrintInvoice = (row) => {
    openReceiptReportByPayRow(row);
  };

  const handleAddInvoice = (invoiceData) => {
    console.log('handleAddInvoice ถูกเรียก', invoiceData);
    setInvoiceDataForAdd(invoiceData);
    setShowAddInvoiceModal(true);
  };

  const handleConfirmAddInvoice = async (formData) => {
    try {
      // ใช้ Doc_Id จาก invoice เดิม หรือดึงจาก contract
      let docId = invoiceDataForAdd.Doc_Id;
      if (!docId) {
        const contractRes = await api.get(`/get-contract/${id}`);
        if (contractRes.data.status !== "OK") {
          throw new Error("ไม่สามารถดึงข้อมูลสัญญาได้");
        }
        const header = contractRes.data.header || {};
        docId = header.Doc_Id || id;
      }

      // เรียก API สร้าง Invoice ใหม่
      const createRes = await api.post("/create-contract-invoice", {
        Doc_Id: docId,
        remainingAmount: invoiceDataForAdd.remainingAmount,
        invoiceDate: formData.invoiceDate,
        dueDate: formData.dueDate,
        sourceInvoice: {
          Invoice_No: invoiceDataForAdd.Invoice_No,
          Line: invoiceDataForAdd.Line,
          CT_Due_Date: invoiceDataForAdd.CT_Due_Date,
        },
      });

      if (createRes.data.status !== "OK") {
        throw new Error(createRes.data.error || "สร้าง Invoice ใหม่ไม่สำเร็จ");
      }

      // รีเฟรชข้อมูล
      fetchInvoiceData();
    } catch (error) {
      console.error("Error creating invoice:", error);
      throw error;
    }
  };

  if (loading || !data) {
    return (
      <div className="flex">
        <Sidebar />
        <div
          className="flex-1 min-h-screen bg-gray-50"
          style={{ paddingLeft: "var(--sidebar-width)" }}
        >
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E13F57]"></div>
              <p className="mt-2 text-gray-600">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">

      {/* SIDEBAR */}
      <Sidebar />

      {/* MAIN (ตัวจริง) */}
      <main
        className="flex-1 h-screen overflow-auto bg-gray-50 transition-all duration-300"
        style={{ marginLeft: "var(--sidebar-width)" }}
      >
        {/* TOP BAR */}
        <Topbar
          breadcrumbs={[
            { label: "แฟ้มงาน", link: "/jobfile" },
            { label: "รายการแฟ้มงาน", link: "/jobfile" },
            { label: data.Contract_No },
          ]}
        />

        {/* TABS (STICKY) */}
        <div className="sticky top-14 z-20 bg-white border-b">
          <CustomerTabsJobFile job={{ Contract_No: data.Contract_No }} />
        </div>

        {/* TITLE */}
        <div className="px-8 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">
              ใบแจ้งหนี้ทั้งหมดของสัญญา {data.Contract_No}
            </h1>
            <button
              onClick={() => navigate(`/jobfile/${id}/invoice-record`)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#E13F57] rounded-lg hover:bg-[#c9354a] transition-colors"
            >
              <Icon icon="mdi:file-document-edit" className="w-5 h-5" />
              บันทึกการชำระเงิน
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="px-8 pb-20">
          <JobFileInvoiceTable
            invoices={invoices}
            onEdit={handleEditInvoice}
            onAddInvoice={handleAddInvoice}
            onPrint={handlePrintInvoice}
            onDelete={async (row) => {
              const result = await Swal.fire({
                title: "ยืนยันการลบ",
                text: `คุณต้องการลบใบแจ้งหนี้ ${row.Invoice_No} ใช่หรือไม่?`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "ลบ",
                cancelButtonText: "ยกเลิก",
              });

              if (result.isConfirmed) {
                try {
                  const deleteRes = await api.delete(`/delete-contract-invoice/${row.CT_Pay_Id}`);
                  if (deleteRes.data.status !== "OK") {
                    throw new Error(deleteRes.data.error || "ลบใบแจ้งหนี้ไม่สำเร็จ");
                  }

                  fetchInvoiceData();
                } catch (error) {
                  console.error("Error deleting invoice:", error);
                  Swal.fire({
                    icon: "error",
                    title: "เกิดข้อผิดพลาด",
                    text: error.message || "ไม่สามารถลบใบแจ้งหนี้ได้",
                  });
                }
              }
            }}
          />
        </div>
      </main>

      {/* INVOICE EDIT MODAL */}
      {showInvoiceModal && selectedInvoice && (
        <InvoiceEditModal
          isOpen={showInvoiceModal}
          onClose={handleModalClose}
          invoice={selectedInvoice}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* ADD INVOICE MODAL */}
      {showAddInvoiceModal && invoiceDataForAdd && (
        <AddInvoiceModal
          isOpen={showAddInvoiceModal}
          onClose={() => {
            setShowAddInvoiceModal(false);
            setInvoiceDataForAdd(null);
          }}
          invoiceData={invoiceDataForAdd}
          onConfirm={handleConfirmAddInvoice}
        />
      )}
    </div>
  );
}
